"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Dpa = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true });
    if (!Array.isArray(items)) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  };

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const SubSection = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4 ml-0">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">
        {String(t(titleKey))}
      </h3>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  const P = ({ keyName }: { keyName: string }) => <p>{String(t(keyName))}</p>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("dpa.title"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {String(t("dpa.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <div className="mb-8 p-4 rounded-lg bg-muted/50 text-muted-foreground text-sm space-y-1">
              <p>{String(t("dpa.intro"))}</p>
              <p className="font-medium text-foreground mt-2">{String(t("dpa.processor"))}</p>
              <p>{String(t("dpa.processorAddress"))}</p>
              <p>{String(t("dpa.processorSiren"))}</p>
              <p>{String(t("dpa.processorVat"))}</p>
              <p className="italic mt-1">{String(t("dpa.processorLabel"))}</p>
              <p className="mt-2">{String(t("dpa.and"))}</p>
              <p className="font-medium text-foreground">{String(t("dpa.controllerLabel"))}</p>
              <p className="italic">{String(t("dpa.controllerLabel2"))}</p>
            </div>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="dpa.section1Title">
                <P keyName="dpa.section1P1" />
                <P keyName="dpa.section1P2" />
              </Section>

              <Section titleKey="dpa.section2Title">
                {renderList("dpa.section2Defs")}
              </Section>

              <Section titleKey="dpa.section3Title">
                <P keyName="dpa.section3P1" />
                <P keyName="dpa.section3P2" />
                {renderList("dpa.section3List")}
              </Section>

              <Section titleKey="dpa.section4Title">
                <P keyName="dpa.section4P1" />
                <P keyName="dpa.section4P2" />
                {renderList("dpa.section4List")}
              </Section>

              <Section titleKey="dpa.section5Title">
                <P keyName="dpa.section5P1" />
                {renderList("dpa.section5List")}
                <P keyName="dpa.section5P2" />
              </Section>

              <Section titleKey="dpa.section6Title">
                <P keyName="dpa.section6P1" />
                {renderList("dpa.section6List")}
                <P keyName="dpa.section6P2" />
              </Section>

              <Section titleKey="dpa.section7Title">
                {renderList("dpa.section7List")}
              </Section>

              <Section titleKey="dpa.section8Title">
                <P keyName="dpa.section8P1" />
                {renderList("dpa.section8List")}
              </Section>

              <Section titleKey="dpa.section9Title">
                <P keyName="dpa.section9P1" />
                <SubSection titleKey="dpa.section9Infra">
                  {renderList("dpa.section9InfraList")}
                </SubSection>
                <SubSection titleKey="dpa.section9Enc">
                  {renderList("dpa.section9EncList")}
                </SubSection>
                <SubSection titleKey="dpa.section9Access">
                  {renderList("dpa.section9AccessList")}
                </SubSection>
                <SubSection titleKey="dpa.section9Org">
                  {renderList("dpa.section9OrgList")}
                </SubSection>
                <P keyName="dpa.section9P2" />
              </Section>

              <Section titleKey="dpa.section10Title">
                <P keyName="dpa.section10P1" />
                {renderList("dpa.section10List")}
                <P keyName="dpa.section10P2" />
              </Section>

              <Section titleKey="dpa.section11Title">
                <P keyName="dpa.section11P1" />
                <P keyName="dpa.section11P2" />
                <P keyName="dpa.section11P3" />
              </Section>

              <Section titleKey="dpa.section12Title">
                <P keyName="dpa.section12P1" />
                {renderList("dpa.section12List")}
              </Section>

              <Section titleKey="dpa.section13Title">
                <P keyName="dpa.section13P1" />
                {renderList("dpa.section13List")}
              </Section>

              <Section titleKey="dpa.section14Title">
                <P keyName="dpa.section14P1" />
                <P keyName="dpa.section14P2" />
                {renderList("dpa.section14List")}
              </Section>

              <Section titleKey="dpa.section15Title">
                <P keyName="dpa.section15P1" />
                {renderList("dpa.section15List")}
              </Section>

              <Section titleKey="dpa.section16Title">
                <P keyName="dpa.section16P1" />
                <P keyName="dpa.section16P2" />
              </Section>

              <Section titleKey="dpa.section17Title">
                <P keyName="dpa.section17P1" />
                <P keyName="dpa.section17P2" />
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Dpa;
